import { useEffect, useMemo, useState } from 'react';
import { Plus, Route as RouteIcon, CalendarClock, Clock, CheckCircle2, Search } from 'lucide-react';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { TripsTable } from '../../components/trips/TripsTable';
import { TripDetailModal } from '../../components/trips/TripDetailModal';
import { TripFormModal } from '../../components/trips/TripFormModal';
import { TripCancelModal } from '../../components/trips/TripCancelModal';
import { TripMomentModal } from '../../components/trips/TripMomentModal';
import { useTrips } from '../../hooks/useTrips';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { useAuth } from '../../context/AuthContext';
import type { Trip, TripStatus } from '../../types/trip';
import {
  TRIP_DATE_FILTERS,
  TRIP_STATUS_LABELS,
  mensajeErrorViaje,
  puedeProgramar,
  rangoDeFiltro,
  type TripDateFilter,
} from '../../utils/trips';

const ESTADOS: { value: TripStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  ...(Object.keys(TRIP_STATUS_LABELS) as TripStatus[]).map(s => ({ value: s, label: TRIP_STATUS_LABELS[s] })),
];

/**
 * FE-11 — gestión de viajes.
 *
 * El backend ya acota la visibilidad por rol en `GET /trips` (el Supervisor ve
 * su sub-unidad, el Ejecutivo el consolidado), así que esta página no vuelve a
 * filtrar por tenant ni por sub-unidad: hacerlo sería fingir una autorización
 * de cliente. Lo que sí decide el rol es qué BOTONES existen — y aun así el
 * servidor puede responder 403, que se muestra como mensaje.
 */
export default function TripsPage() {
  const { user } = useAuth();
  const [dateFilter, setDateFilter] = useState<TripDateFilter>('month');
  const [statusFilter, setStatusFilter] = useState<TripStatus | 'all'>('all');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [search, setSearch] = useState('');

  const filtro = useMemo(() => ({
    ...rangoDeFiltro(dateFilter),
    status: statusFilter === 'all' ? undefined : statusFilter,
    vehicleId: vehicleFilter || undefined,
  }), [dateFilter, statusFilter, vehicleFilter]);

  const { trips, isLoading, error, programar, corregir, iniciar, finalizar, cancelar } = useTrips(filtro);
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [corrigiendoId, setCorrigiendoId] = useState<string | null>(null);
  const [cancelandoId, setCancelandoId] = useState<string | null>(null);
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);
  const [avisoError, setAvisoError] = useState<string | null>(null);
  const [momento, setMomento] = useState<{ trip: Trip; accion: 'iniciar' | 'finalizar' } | null>(null);
  const [errorMomento, setErrorMomento] = useState<string | null>(null);

  useEffect(() => { document.title = 'Viajes | Paitron Radar'; }, []);

  // Los tres modales trabajan sobre el viaje VIVO de la lista, no sobre una
  // copia congelada al abrirlos: tras corregir, el objeto cambia de ventana y
  // el expediente del detalle tiene que volver a pedirse solo (ADR-024).
  const selectedTrip = trips.find(t => t.id === selectedId) ?? null;
  const tripCorrigiendo = trips.find(t => t.id === corrigiendoId) ?? null;
  const tripCancelando = trips.find(t => t.id === cancelandoId) ?? null;

  const visibles = trips.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.origin.toLowerCase().includes(q) ||
      t.destination.toLowerCase().includes(q) ||
      t.vehiclePlate.toLowerCase().includes(q) ||
      (t.driverName ?? '').toLowerCase().includes(q)
    );
  });

  const conteo = (s: TripStatus) => trips.filter(t => t.status === s).length;

  // Abrir Iniciar/Finalizar pide el instante (por defecto "ahora"): así se puede
  // declarar una salida/llegada pasada para registrar un viaje ya realizado (#190).
  const pedirMomento = (trip: Trip, accion: 'iniciar' | 'finalizar') => {
    setAvisoError(null);
    setErrorMomento(null);
    setMomento({ trip, accion });
  };
  const confirmarMomento = async (at?: string) => {
    if (!momento) return;
    const { trip, accion } = momento;
    setAccionEnCurso(trip.id);
    setErrorMomento(null);
    try {
      await (accion === 'iniciar' ? iniciar(trip.id, at) : finalizar(trip.id, at));
      setMomento(null);
    } catch (err) {
      setErrorMomento(mensajeErrorViaje(err, accion)); // se queda abierto para corregir la hora
    } finally {
      setAccionEnCurso(null);
    }
  };

  return (
    <div className="trips-page" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {[
          { icon: <CalendarClock size={22} />, color: '#8B5CF6', valor: conteo('planned'), label: 'Programados' },
          { icon: <Clock size={22} />, color: '#3B82F6', valor: conteo('in_progress'), label: 'En curso' },
          { icon: <CheckCircle2 size={22} />, color: '#10B981', valor: conteo('completed'), label: 'Completados' },
          { icon: <RouteIcon size={22} />, color: 'var(--accent)', valor: trips.length, label: 'Viajes en el periodo' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: `${s.color}1F`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {s.icon}
            </div>
            <div>
              <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{s.valor}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px' }}>
          <Input
            label="Buscar"
            placeholder="Origen, destino, placa o conductor"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leftIcon={<Search size={16} />}
          />
        </div>
        <div style={{ flex: '0 1 180px' }}>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as TripStatus | 'all')}
            options={ESTADOS}
          />
        </div>
        <div style={{ flex: '0 1 200px' }}>
          <Select
            label="Vehículo"
            value={vehicleFilter}
            onChange={e => setVehicleFilter(e.target.value)}
            options={[{ value: '', label: 'Todos' }, ...vehicles.map(v => ({ value: v.id, label: v.plate }))]}
          />
        </div>
        <div style={{ flex: '0 1 180px' }}>
          <Select
            label="Periodo"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value as TripDateFilter)}
            options={TRIP_DATE_FILTERS.map(f => ({ value: f.id, label: f.label }))}
          />
        </div>
        {puedeProgramar(user?.role) && (
          <Button leftIcon={<Plus size={16} />} onClick={() => { setAvisoError(null); setFormAbierto(true); }}>Programar viaje</Button>
        )}
      </div>

      {avisoError && (
        <p style={{ margin: 0, padding: '0.75rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '13px', fontWeight: 600 }}>
          {avisoError}
        </p>
      )}

      {error ? (
        <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-surface)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-xl)', color: '#EF4444' }}>
          No se pudieron cargar los viajes: {error.message}
        </div>
      ) : isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Spinner /></div>
      ) : visibles.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', color: 'var(--text-muted)' }}>
          No hay viajes que coincidan con los filtros.
        </div>
      ) : (
        <TripsTable
          trips={visibles}
          role={user?.role}
          accionEnCurso={accionEnCurso}
          onVerDetalle={t => setSelectedId(t.id)}
          onIniciar={t => pedirMomento(t, 'iniciar')}
          onFinalizar={t => pedirMomento(t, 'finalizar')}
          onCorregir={t => { setAvisoError(null); setCorrigiendoId(t.id); }}
          onCancelar={t => { setAvisoError(null); setCancelandoId(t.id); }}
        />
      )}

      <TripDetailModal
        isOpen={selectedTrip !== null}
        onClose={() => setSelectedId(null)}
        trip={selectedTrip}
        role={user?.role}
        onCorregir={t => { setSelectedId(null); setCorrigiendoId(t.id); }}
        onCancelar={t => { setSelectedId(null); setCancelandoId(t.id); }}
        onIniciar={t => pedirMomento(t, 'iniciar')}
        onFinalizar={t => pedirMomento(t, 'finalizar')}
      />

      <TripMomentModal
        isOpen={momento !== null}
        accion={momento?.accion ?? 'iniciar'}
        trip={momento?.trip ?? null}
        isLoading={accionEnCurso === momento?.trip.id}
        error={errorMomento}
        canBackdate={user?.role === 'admin' || user?.role === 'supervisor'}
        onConfirm={confirmarMomento}
        onClose={() => { setMomento(null); setErrorMomento(null); }}
      />

      <TripFormModal
        isOpen={formAbierto || tripCorrigiendo !== null}
        onClose={() => { setFormAbierto(false); setCorrigiendoId(null); }}
        trip={tripCorrigiendo}
        vehicles={vehicles}
        drivers={drivers}
        onProgramar={programar}
        onCorregir={corregir}
      />

      <TripCancelModal
        isOpen={tripCancelando !== null}
        onClose={() => setCancelandoId(null)}
        trip={tripCancelando}
        onCancelar={cancelar}
      />
    </div>
  );
}
